import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XMarkIcon, MinusIcon } from '@heroicons/react/24/solid';

type Verdict = 'strong' | 'partial' | 'absent';

const tiers = [
  {
    key: 'lumen8',
    name: 'Lumen8',
    tagline: 'Adaptive mastery platform',
    examples: '',
    highlight: true,
  },
  {
    key: 'legacy',
    name: 'Legacy LMS & Infrastructure',
    tagline: '',
    examples: 'Pure utilities. Locked in slow B2B cycles. 0% consumer monetisation.',
    highlight: false,
  },
  {
    key: 'b2c',
    name: 'B2C Gamification & Tutoring',
    tagline: '',
    examples: 'Mass consumer awareness. Bleeding capital on retail ad spend / high CAC.',
    highlight: false,
  },
  {
    key: 'curriculum',
    name: 'Curriculum & Adaptive Learning',
    tagline: '',
    examples: 'Single-subject silos. Rigid, classroom-moderated textbook digitisation.',
    highlight: false,
  },
] as const;

type TierKey = (typeof tiers)[number]['key'];

const capabilities: Array<{
  title: string;
  description: string;
  verdicts: Record<TierKey, Verdict>;
}> = [
  {
    title: 'Curriculum-aligned content',
    description: 'Built around the Australian Curriculum',
    verdicts: { lumen8: 'strong', legacy: 'partial', b2c: 'absent', curriculum: 'strong' },
  },
  {
    title: 'Multi-Subject Adaptive Engine',
    description: 'Proprietary question engine, not a chatbot wrapper',
    verdicts: { lumen8: 'strong', legacy: 'absent', b2c: 'partial', curriculum: 'partial' },
  },
  {
    title: 'Multi-year mastery tracking',
    description: 'Profile follows the student across years & schools',
    verdicts: { lumen8: 'strong', legacy: 'absent', b2c: 'absent', curriculum: 'partial' },
  },
  {
    title: 'Live parent dashboards',
    description: 'Continuous home visibility & engagement',
    verdicts: { lumen8: 'strong', legacy: 'absent', b2c: 'partial', curriculum: 'absent' },
  },
  {
    title: 'Teacher admin automation',
    description: 'Grading, lesson planning & differentiation',
    verdicts: { lumen8: 'strong', legacy: 'partial', b2c: 'partial', curriculum: 'partial' },
  },
  {
    title: 'Modern, engaging student UX',
    description: 'Daily-use loop & rewards engine',
    verdicts: { lumen8: 'strong', legacy: 'absent', b2c: 'strong', curriculum: 'partial' },
  },
  {
    title: 'LMS/SIS Native Integration',
    description: 'SIS / LMS integration, school-wide rollout',
    verdicts: { lumen8: 'strong', legacy: 'strong', b2c: 'absent', curriculum: 'partial' },
  },
  {
    title: 'Sovereign AI & data residency',
    description: 'Local residency, zero-retention compliance',
    verdicts: { lumen8: 'strong', legacy: 'partial', b2c: 'absent', curriculum: 'partial' },
  },
];

const VerdictMark: React.FC<{ verdict: Verdict }> = ({ verdict }) => {
  if (verdict === 'strong') {
    return <CheckIcon className="w-5 h-5 text-[#10b981]" />;
  }
  if (verdict === 'absent') {
    return <XMarkIcon className="w-5 h-5 text-[#ef4444]" />;
  }
  return <MinusIcon className="w-5 h-5 text-[#64748b]" />;
};

const FFSlideCompetitiveMatrix: React.FC = () => {
  const gridCols = 'grid-cols-[2.2fr_1fr_1.1fr_1.1fr_1.1fr]';

  return (
    <div className="flex flex-col items-center justify-center h-full px-10 py-6">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-bold text-white mb-2 text-center"
      >
        Every rival owns a corner.{' '}
        <span className="bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent">
          Lumen8 owns the intersection.
        </span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-base text-[#94a3b8] mb-6 text-center max-w-4xl"
      >
        The EdTech market splits into three incumbent camps — each strong in one dimension and
        structurally weak in the others. Lumen8 is the only platform engineered to be complete
        across all of them.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-6xl rounded-xl overflow-hidden"
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* Header row */}
        <div className={`grid ${gridCols}`}>
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-[#64748b]">
            Capability
          </div>
          {tiers.map((tier) => (
            <div
              key={tier.key}
              className="px-3 py-3 text-center flex flex-col justify-center"
              style={
                tier.highlight
                  ? {
                      background:
                        'linear-gradient(180deg, rgba(14, 165, 233, 0.25) 0%, rgba(6, 182, 212, 0.15) 100%)',
                      borderLeft: '1px solid rgba(14, 165, 233, 0.3)',
                      borderRight: '1px solid rgba(14, 165, 233, 0.3)',
                    }
                  : undefined
              }
            >
              {tier.highlight && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#0ea5e9] mb-0.5">
                  Lumen8
                </div>
              )}
              <div className="text-sm font-semibold text-white leading-tight">
                {tier.highlight ? tier.tagline : tier.name}
              </div>
              {!tier.highlight && tier.examples && (
                <div className="text-[10px] text-[#64748b] mt-1 leading-snug">
                  {tier.examples}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Capability rows */}
        {capabilities.map((cap, i) => (
          <div
            key={cap.title}
            className={`grid ${gridCols} border-t border-white/5`}
            style={{
              background: i % 2 === 1 ? 'rgba(30, 41, 59, 0.3)' : 'transparent',
            }}
          >
            <div className="px-4 py-2.5">
              <div className="text-sm font-semibold text-white leading-tight">{cap.title}</div>
              <div className="text-[11px] text-[#64748b] leading-snug mt-0.5">
                {cap.description}
              </div>
            </div>
            {tiers.map((tier) => (
              <div
                key={tier.key}
                className="px-3 py-2.5 flex items-center justify-center"
                style={
                  tier.highlight
                    ? {
                        background: 'rgba(14, 165, 233, 0.08)',
                        borderLeft: '1px solid rgba(14, 165, 233, 0.3)',
                        borderRight: '1px solid rgba(14, 165, 233, 0.3)',
                      }
                    : undefined
                }
              >
                <VerdictMark verdict={cap.verdicts[tier.key]} />
              </div>
            ))}
          </div>
        ))}
      </motion.div>

      {/* Legend + footnote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-6xl mt-4 flex items-center justify-between gap-6 flex-wrap"
      >
        <div className="flex items-center gap-5 text-xs text-[#94a3b8]">
          <div className="flex items-center gap-1.5">
            <CheckIcon className="w-4 h-4 text-[#10b981]" />
            <span>
              <span className="font-semibold text-white">Strong</span>
              <span className="text-[#64748b]"> · native capability</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MinusIcon className="w-4 h-4 text-[#64748b]" />
            <span>
              <span className="font-semibold text-white">Partial</span>
              <span className="text-[#64748b]"> · limited or scripted</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <XMarkIcon className="w-4 h-4 text-[#ef4444]" />
            <span>
              <span className="font-semibold text-white">Absent</span>
              <span className="text-[#64748b]"> · not addressed</span>
            </span>
          </div>
        </div>
        <div className="text-[10px] text-[#475569] max-w-md text-right">
          Competitor names are indicative of each category and used for illustration only.
          Assessment reflects Lumen8's review of publicly available product positioning as at
          May 2026.
        </div>
      </motion.div>
    </div>
  );
};

export default FFSlideCompetitiveMatrix;
